<template>
    <div class="row">
        <div class="col-md-12">
            <div class="card">
                <div class="card-header">
                    <span class="card-title p-0 m-0">Schedule Management
                        <button type="button" data-bs-toggle="modal" data-bs-target="#hmMarkLeave" class="btn btn-sm btn-outline-theme float-end"><i class="bi bi-plus-circle pe-1"></i> Apply Leave</button>
                    </span>
                </div>
                <div class="card-body">
                    <div v-if="processing" class="text-center py-5">
                        <div class="spinner-border text-theme" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading schedules...</p>
                    </div>

                    <div v-else>
                        <div class="table-responsive">
                            <table class="table align-middle datatable w-100 table-hover table-sm">
                                <thead>
                                    <tr>
                                        <th scope="col">Name</th>
                                        <th scope="col" class="text-center">Monday</th>
                                        <th scope="col" class="text-center">Tuesday</th>
                                        <th scope="col" class="text-center">Wednesday</th>
                                        <th scope="col" class="text-center">Thursday</th>
                                        <th scope="col" class="text-center">Friday</th>
                                        <th scope="col" class="text-center">Saturday</th>
                                        <th scope="col" class="text-center">Sunday</th>
                                        <th scope="col" class="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="schedules.length === 0">
                                        <td :colspan="daysOfWeek.length + 2" class="text-center">No handymen found.</td>
                                    </tr>
                                    <tr v-for="handyman in schedules" :key="handyman.id">
                                        <td>
                                            {{ handyman.name }}
                                            <template v-if="handyman.roles">
                                                <span 
                                                    v-for="(role, index) in handyman.roles" 
                                                    :key="role.id" 
                                                    class="badge bg-theme"
                                                    :class="{'me-1': index < handyman.roles.length - 1}"
                                                >{{ role.name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) }}</span>
                                            </template>
                                        </td>
                                        <td v-for="day in daysOfWeek" :key="`${handyman.id}-${day}`" class="text-center">
                                            {{ getUserSchedule(handyman)[day] }}
                                        </td>
                                        <td class="text-center">
                                            <div class="d-flex gap-2 align-items-center justify-content-center">
                                                <a class="datatable-action-item cursor-pointer" title="Edit Schedule" @click.prevent="editSchedule(handyman)">
                                                    <i class="bi bi-pencil"></i>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit schedule modal -->
        <div class="modal fade" id="editScheduleModal" tabindex="-1" aria-labelledby="editScheduleModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="editScheduleModalLabel">Edit Schedule</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body pt-0">
                        <div class="row mt-1 g-3">
                            Updating the schedule for {{ scheduleData.name }}
                            <table class="table table-bordered align-middle">
                                <thead>
                                    <tr>
                                        <th scope="col" width="20%">Day</th>
                                        <th scope="col" width="70%">Working Hours</th>
                                        <th scope="col" class="text-center" width="10%">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="schedule in scheduleData.schedules" :key="schedule.day_of_week">
                                        <td>{{ schedule.day_of_week.toUpperCase() }}</td>
                                        <td>
                                            <div class="d-flex gap-3 align-items-center">
                                                <!-- Check if is_working_day is true -->
                                                <template v-if="!schedule.is_working_day">
                                                    Closed
                                                    <i 
                                                        class="bi bi-plus-circle cursor-pointer"
                                                        title="Add Working Hours"
                                                        @click="schedule.is_working_day = true"
                                                    ></i>
                                                </template>
                                                <template v-else>
                                                    <VueSelect
                                                        v-model="schedule.start_time"
                                                        :name="'start_time_'+schedule.day_of_week"
                                                        :items="workingTimeOptions"
                                                        :placeholder="'Start Time'"
                                                        :searchable="true"
                                                        :class="['w-100']"
                                                    />
                                                    <div>-</div>
                                                    <VueSelect
                                                        v-model="schedule.end_time"
                                                        :name="'end_time_'+schedule.day_of_week"
                                                        :items="workingTimeOptions"
                                                        :placeholder="'End Time'"
                                                        :searchable="true"
                                                        :class="['w-100']"
                                                    />
                                                    <i 
                                                        class="bi bi-x-circle cursor-pointer text-danger"
                                                        title="Remove Working Hours"
                                                        @click="schedule.is_working_day = false"
                                                    ></i>
                                                </template>
                                            </div>
                                        </td>
                                        <td class="text-center position-relative">
                                            <a 
                                                v-if="schedule.is_working_day"
                                                class="me-2 datatable-action-item cursor-pointer d-inline-block" 
                                                title="Edit Schedule" 
                                                data-bs-toggle="dropdown"
                                            >
                                                <i class="bi bi-copy cursor-pointer"></i>
                                            </a>
                                            <ul class="dropdown-menu dropdown-menu-end">
                                                <li class="p-2"><a class="dropdown-item p-0" href="#">Copy to selected days</a></li>
                                                <li 
                                                    v-for="day in copyToDays" 
                                                    :key="day.dayOfWeek"
                                                    class="px-2"
                                                    @click.stop
                                                >
                                                    <div class="form-check">
                                                        <template v-if="day.dayOfWeek === schedule.day_of_week">
                                                            <input 
                                                                type="checkbox" 
                                                                class="form-check-input" 
                                                                :id="'copyToDay_'+day.dayOfWeek"
                                                                checked
                                                                disabled
                                                            >
                                                        </template>
                                                        <template v-else>
                                                            <input 
                                                                type="checkbox" 
                                                                class="form-check-input" 
                                                                :id="'copyToDay_'+day.dayOfWeek"
                                                                :value="day.dayOfWeek"
                                                                v-model="day.checked"
                                                            >
                                                        </template>
                                                        <label class="form-check-label" :for="'copyToDay_'+day.dayOfWeek">{{ day.name }}</label>
                                                    </div>
                                                </li>
                                                <li class="p-2">
                                                    <button 
                                                        type="button" 
                                                        class="btn btn-theme btn-sm w-100"
                                                        @click="copyToSelectedDays(schedule)"
                                                    >
                                                        Apply
                                                    </button>
                                                </li>
                                            </ul>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-danger" data-bs-toggle="modal" data-bs-target="#editScheduleModal">Cancel</button>
                        <button
                            type="button"
                            class="btn btn-theme"
                            @click="updateSchedule"
                            :disabled="processing"
                            :class="{ 'disabled cursor-not-allowed': processing }"
                            v-text="(processing ? 'Saving...' : 'Save')"
                        ></button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import {appMixin} from '../../mixin/appMixin';
import VueSelect from '../partials/VueSelect';
export default {
    name: 'WorkingSchedules',
    mixins: [appMixin],
    components: {
        VueSelect
    },
    data() {
        return {
            processing: false,
            users: [],
            agencySchedules: [],
            daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
            scheduleData: {},
            copyToDays:[
                { dayOfWeek: 'monday', name: 'Monday', checked: false },
                { dayOfWeek: 'tuesday', name: 'Tuesday', checked: false },
                { dayOfWeek: 'wednesday', name: 'Wednesday', checked: false },
                { dayOfWeek: 'thursday', name: 'Thursday', checked: false },
                { dayOfWeek: 'friday', name: 'Friday', checked: false },
                { dayOfWeek: 'saturday', name: 'Saturday', checked: false },
                { dayOfWeek: 'sunday', name: 'Sunday', checked: false },
            ],
        }
    },
    mounted() {
        this.getSchedules();
    },
    computed: {
        schedules() {
            // Prepare schedules for display
            // Use handyman's custom schedules if available, otherwise fall back to agency schedules
            return this.users.map(user => {
                const schedulesToUse = user.custom_schedules && user.custom_schedules.length > 0
                    ? user.custom_schedules
                    : this.agencySchedules;
                
                return {
                    ...user,
                    schedules: schedulesToUse.map(schedule => ({
                        ...schedule,
                        day_of_week: schedule.day_of_week.toLowerCase()
                    }))
                };
            });
        },
        userSchedules() {
            return this.schedules.reduce((acc, user) => {
                acc[user.id] = this.daysOfWeek.reduce((days, day) => {
                    // Check for custom schedule first
                    let schedule = user.schedules?.find(s => 
                        s.day_of_week.toLowerCase() === day
                    );

                    days[day] = !schedule || !schedule.is_working_day 
                        ? 'Closed' 
                        : this.formatTimeRange(schedule.start_time, schedule.end_time);
                    
                    return days;
                }, {});
                return acc;
            }, {});
        }
    },
    methods: {
        getSchedules() {
            this.processing = true;
            axios.get('/api/admin/staff/schedules')
                .then(response => {
                    this.processing = false;
                    this.agencySchedules = response.data.agencySchedules;
                    this.users = response.data.users;

                    // Display update schedule modal if staff_id is present in URL
                    this.displayUpdateScheduleModal();
                })
                .catch(error => {
                    this.processing = false;
                    console.error('Error fetching schedules:', error);
                    // You might want to show an error toast/alert here
                });   
        },
        getUserSchedule(user) {
            return this.userSchedules[user.id] || {};
        },
        formatTimeRange(start, end) {
            if (!start || !end) return 'Closed';

            return `${this.momentObj(start, 'HH:mm:ss').format('hh A')} - ${this.momentObj(end, 'HH:mm:ss').format('hh A')}`;
        },
        copyToSelectedDays(schedule) {
            const startTime = schedule.start_time;
            const endTime = schedule.end_time;

            // Get all checked days
            // And set the start time and end time for each checked day
            const checkedDays = this.copyToDays.filter(day => day.checked).map(day => day.dayOfWeek);
            console.log(checkedDays);
            checkedDays.forEach(day => {
                this.scheduleData.schedules.map(schedule => {
                    if(schedule.day_of_week.toLowerCase() === day) {
                        schedule.start_time = startTime;
                        schedule.end_time = endTime;
                    }
                });
            });

            // Uncheck all days
            this.copyToDays.forEach(day => {
                day.checked = false;
            });
        },
        editSchedule(handyman) {
            this.scheduleData = handyman;
            // Display edit schedule modal
            $('#editScheduleModal').modal('show');
        },
        updateSchedule() {
            this.processing = true;
            axios.post('/api/admin/staff/update-schedule', {
                handyman_id: this.scheduleData.id,
                name: this.scheduleData.name,
                email: this.scheduleData.email,
                schedules: this.scheduleData.schedules
            })
                .then(response => {
                    this.processing = false;
                    $('#editScheduleModal').modal('hide');
                    this.getSchedules();
                })
                .catch(error => {
                    this.processing = false;
                    console.error('Error updating schedule:', error);
                    // You might want to show an error toast/alert here
                });
        },
        displayUpdateScheduleModal() {
            // Get the URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const staffId = urlParams.get('staff_id');
            if (staffId) {
                // Find the user with the given staff_id
                const user = this.schedules.find(user => user.id === parseInt(staffId));
                if (user) {
                    this.$nextTick(() => {
                        this.editSchedule(user);

                        // Remove the staff_id from the URL
                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.delete('staff_id');
                        const newUrl = window.location.pathname + '?' + urlParams.toString();
                        window.history.replaceState(null, '', newUrl);
                    });
                }
            }
        }
    }
}
</script>

<style scoped>

</style>